import { useMemo, useState } from 'react'
import type { Question, QuestionResult, Source, SupportSettings } from '../types'
import { detectKeywords } from '../utils/text'
import { shuffle } from '../utils/random'
import { Stimulus } from './Stimulus'

interface Props {
  question: Question
  source?: Source
  support: SupportSettings
  onComplete: (result: QuestionResult) => void
}

function answersEqual(answer: unknown, correct: unknown): boolean {
  if (Array.isArray(answer) && Array.isArray(correct)) return answer.length === correct.length && answer.every((item, index) => item === correct[index])
  if (answer && correct && typeof answer === 'object' && typeof correct === 'object') return JSON.stringify(answer) === JSON.stringify(correct)
  return answer === correct
}

function specificHint(question: Question, guided: boolean): string {
  if (!guided) return question.hint
  if (question.stimulusType === 'table' || question.stimulusType === 'chart') return `Observa el cambio más importante de los datos. ${question.hint}`
  if (question.stimulusType === 'image' || question.imagePath) return `Fíjate en las personas, acciones y símbolos de la imagen. ${question.hint}`
  if (question.stimulus || question.sourceId) return `Vuelve a la fuente e identifica la idea que responde directamente la pregunta. ${question.hint}`
  if (question.skill.toLowerCase().includes('causa')) return `Busca qué hecho ocurrió primero y qué consecuencia produjo. ${question.hint}`
  if (question.skill.toLowerCase().includes('compar')) return `Compara qué elemento se mantiene y cuál cambia. ${question.hint}`
  return `Recuerda el concepto central de “${question.topic}”. ${question.hint}`
}

export function QuestionCard({ question, source, support, onComplete }: Props) {
  const initialOrder = useMemo(() => question.type === 'ordering' ? shuffle(question.options) : question.options, [question.options, question.type])
  const matchingKeys = useMemo(() => question.type === 'matching' ? Object.keys(question.correctAnswer as Record<string, string>) : [], [question.correctAnswer, question.type])
  const [selected, setSelected] = useState('')
  const [lastIncorrect, setLastIncorrect] = useState('')
  const [order, setOrder] = useState<string[]>(initialOrder)
  const [matches, setMatches] = useState<Record<string, string>>({})
  const [development, setDevelopment] = useState('')
  const [selfAssessment, setSelfAssessment] = useState<QuestionResult['selfAssessment']>()
  const [attempts, setAttempts] = useState(0)
  const [resolved, setResolved] = useState(false)
  const [correct, setCorrect] = useState<boolean | null>(null)
  const [message, setMessage] = useState('')
  const [detected, setDetected] = useState<string[]>([])
  const [missing, setMissing] = useState<string[]>([])

  const displayedPrompt = (support.shortPrompts || support.simplifiedVocabulary) && question.adaptedPrompt ? question.adaptedPrompt : question.prompt
  const currentAnswer = () => question.type === 'ordering' ? order : question.type === 'matching' ? matches : selected
  const longestOption = Math.max(0, ...question.options.map((option) => option.length))
  const longOptions = longestOption > 105 || question.options.some((option) => option.split(' ').length > 17)
  const hasLargeStimulus = Boolean(question.imagePath || question.stimulusType === 'table' || question.stimulusType === 'chart')

  const submitObjective = () => {
    const answer = currentAnswer()
    const nextAttempt = attempts + 1
    setAttempts(nextAttempt)
    if (answersEqual(answer, question.correctAnswer)) {
      setCorrect(true)
      setResolved(true)
      setMessage('¡Correcto! Revisa la explicación antes de continuar.')
      return
    }
    if (typeof answer === 'string') setLastIncorrect(answer)
    if (nextAttempt === 1) {
      setSelected('')
      setMessage(`Pista: ${specificHint(question, support.specificHints)}`)
      return
    }
    setCorrect(false)
    setResolved(true)
    setMessage('Todavía no es correcto. Lee la explicación y continúa cuando estés lista o listo.')
  }

  const submitDevelopment = () => {
    const keywordResult = detectKeywords(development, question.keywords)
    setDetected(keywordResult.detected)
    setMissing(keywordResult.missing)
    setAttempts(1)
    setCorrect(null)
    setResolved(true)
    setMessage('Compara tu respuesta con la pauta. Esta revisión es una ayuda formativa.')
  }

  const canSubmit = question.type === 'development' ? development.trim().length >= 8
    : question.type === 'matching' ? matchingKeys.every((key) => Boolean(matches[key]))
      : question.type === 'ordering' ? order.length > 0 : Boolean(selected)

  const continueSession = () => {
    if (question.type === 'development' && !selfAssessment) return
    onComplete({
      questionId: question.id, oa: question.oa, topic: question.topic, type: question.type, correct, attempts,
      detectedKeywords: question.type === 'development' ? detected : undefined,
      missingKeywords: question.type === 'development' ? missing : undefined,
      selfAssessment: question.type === 'development' ? selfAssessment : undefined,
    })
  }

  const moveItem = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= order.length) return
    const next = [...order]
    ;[next[index], next[target]] = [next[target]!, next[index]!]
    setOrder(next)
  }

  const isChoice = ['multiple_choice', 'true_false', 'source', 'data'].includes(question.type)
  const correctText = Array.isArray(question.correctAnswer)
    ? question.correctAnswer.join(' → ')
    : typeof question.correctAnswer === 'object' ? 'Revisa las relaciones indicadas.' : question.correctAnswer
  const concept = (question.keywords[0]?.split('|')[0] || question.topic).trim()

  return (
    <article className={`question-card v4-question ${support.reducedDistractions ? 'reduced-distractions' : ''} ${hasLargeStimulus ? 'has-stimulus' : ''}`} aria-labelledby="question-title">
      <header className="question-heading">
        <div className="question-meta"><span>{question.oa}</span><span>{question.skill}</span><span>{question.difficulty}</span></div>
        <h1 id="question-title">{displayedPrompt}</h1>
      </header>

      <Stimulus question={question} source={source} showImage={support.visualSupport !== false} shorterSource={support.shorterSources} enlargedImage={support.enlargedImages} />

      {isChoice && (
        <fieldset className={`answer-options ${question.options.length === 4 ? 'four-options' : ''} ${longOptions ? 'long-options' : ''}`} disabled={resolved}>
          <legend className="sr-only">Selecciona una alternativa</legend>
          {question.options.map((option, index) => {
            const optionId = `${question.id}-option-${index}`
            return <label htmlFor={optionId} className={selected === option ? 'answer-option selected' : 'answer-option'} key={option}>
              <input id={optionId} type="radio" name={`question-${question.id}`} value={option} checked={selected === option} onChange={() => setSelected(option)} />
              <span className="option-letter">{String.fromCharCode(65 + index)}</span><span className="option-text">{option}</span>
            </label>
          })}
        </fieldset>
      )}

      {question.type === 'ordering' && (
        <section className="ordering-activity">
          <p className="activity-example"><strong>Ejemplo:</strong> deja el acontecimiento más antiguo arriba y el más reciente abajo.</p>
          <ol className="ordering-list" aria-label="Orden propuesto">
            {order.map((item, index) => <li key={item}><span className="order-number">{index + 1}</span><span>{item}</span><span className="order-buttons"><button type="button" onClick={() => moveItem(index, -1)} disabled={resolved || index === 0} aria-label={`Subir ${item}`}>↑</button><button type="button" onClick={() => moveItem(index, 1)} disabled={resolved || index === order.length - 1} aria-label={`Bajar ${item}`}>↓</button></span></li>)}
          </ol>
        </section>
      )}

      {question.type === 'matching' && (
        <div className="matching-grid">{matchingKeys.map((left) => <label key={left}><span>{left}</span><select value={matches[left] ?? ''} onChange={(event) => setMatches({ ...matches, [left]: event.target.value })} disabled={resolved}><option value="">Selecciona</option>{question.options.map((option) => <option key={option}>{option}</option>)}</select></label>)}</div>
      )}

      {question.type === 'development' && (
        <div className="development-answer v4-development">
          <label htmlFor={`development-${question.id}`}>Responde con ideas y conceptos clave. Puedes usar palabras diferentes a las de la pauta.</label>
          <p className="development-help">No necesitas escribir una definición perfecta. Explica la idea con el vocabulario que manejas.</p>
          <textarea id={`development-${question.id}`} value={development} onChange={(event) => setDevelopment(event.target.value)} disabled={resolved} rows={4} placeholder="Escribe aquí tu explicación…" />
        </div>
      )}

      {message && <div className={resolved ? 'feedback-box resolved' : 'feedback-box hint'} role="status">{message}</div>}

      {resolved && question.type !== 'development' && (
        <section className="explanation v4-explanation" aria-label="Explicación de la respuesta">
          <div className="feedback-title"><strong>Respuesta correcta:</strong> {correctText}</div>
          <p>{question.feedback}</p>
          {!correct && lastIncorrect && question.distractorFeedback[lastIncorrect] && <p className="distractor-note"><strong>Por qué tu alternativa no era correcta:</strong> {question.distractorFeedback[lastIncorrect]}</p>}
          <div className="feedback-facts"><span><strong>Concepto clave:</strong> {concept}</span><span><strong>Debes reforzar:</strong> {question.topic}</span>{source && <span><strong>Fuente:</strong> {source.author || source.origin}, {source.date || 's. f.'}</span>}</div>
        </section>
      )}

      {resolved && question.type === 'development' && (
        <section className="explanation v4-explanation" aria-label="Pauta de autoevaluación">
          <h2>Revisión formativa</h2><p className="formative-note">La plataforma reconoce grupos de conceptos y sinónimos. No exige repetir una frase exacta.</p><p><strong>Conceptos detectados:</strong> {detected.length ? detected.join(', ') : 'Todavía no se identificaron conceptos clave.'}</p>
          <p><strong>Podrías integrar:</strong> {missing.length ? missing.join(', ') : 'Incluiste todos los conceptos sugeridos.'}</p><p><strong>Respuesta esperada:</strong> {question.expectedAnswer}</p>
          <fieldset className="self-assessment"><legend>¿Cómo evalúas tu respuesta?</legend>{(['Lo logré', 'Lo logré parcialmente', 'Necesito reforzarlo'] as const).map((value) => <label key={value}><input type="radio" name="self-assessment" checked={selfAssessment === value} onChange={() => setSelfAssessment(value)} /> {value}</label>)}</fieldset>
        </section>
      )}

      <div className="question-actions">{!resolved
        ? <button type="button" className="primary-button" disabled={!canSubmit} onClick={question.type === 'development' ? submitDevelopment : submitObjective}>{attempts === 0 ? 'Confirmar respuesta' : 'Comprobar segundo intento'}</button>
        : <button type="button" className="primary-button" disabled={question.type === 'development' && !selfAssessment} onClick={continueSession}>Continuar</button>}
      </div>
    </article>
  )
}
